import { useEffect } from "react";
import { clampTitle } from "@/lib/seoText";

// Module-level cache of admin SEO overrides (fetched once).
let _ovCache = null;
async function getOverrides() {
  if (_ovCache) return _ovCache;
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/overrides`);
    _ovCache = await r.json();
  } catch (e) { _ovCache = {}; }
  return _ovCache;
}

/**
 * International SEO helper. Sets document title, meta description, Open Graph,
 * Twitter, canonical, hreflang alternates and optional JSON-LD (Schema.org).
 *
 * Props:
 *  - title, description, image, type
 *  - canonical: absolute canonical URL for THIS language variant
 *  - alternates: [{ hreflang, href }] reciprocal set incl. x-default
 *  - ogLocale, ogUrl, siteName
 *  - jsonLd: object OR array of objects (multiple @graph nodes)
 *  - feedHref: RSS feed URL (autodiscovery)
 *  - articleMeta: { publishedTime, modifiedTime, author, tags, imageAlt } for blog posts
 */
// Trim a meta description to <=150 chars on a word boundary. Char cap kept slightly
// under the ~155 mark so wide-character descriptions also stay within Google's ~985px snippet limit.
function clampDesc(s, max = 150) {
  if (typeof s !== "string") return s;
  const t = s.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const sp = cut.lastIndexOf(" ");
  const base = (sp > 80 ? cut.slice(0, sp) : cut).replace(/[\s,;:.\-–—]+$/, "");
  return base + "…";
}

export default function Seo({
  title, description, image, type = "website",
  canonical, alternates, ogLocale, ogUrl, siteName = "Ma Petite Ferme", jsonLd, noindex, feedHref, articleMeta,
}) {
  useEffect(() => {
    if (title) document.title = clampTitle(title);
    const metaDesc = clampDesc(description);

    const upsertMeta = (key, attr, value) => {
      const sel = `meta[${attr}="${key}"]`;
      let el = document.head.querySelector(sel);
      if (!value) { if (el) el.remove(); return; }
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", value);
    };

    upsertMeta("description", "name", metaDesc);
    upsertMeta("og:title", "property", title);
    upsertMeta("og:description", "property", description);
    upsertMeta("og:type", "property", type);
    const _origin = typeof window !== "undefined" ? window.location.origin : "";
    const absImage = (typeof image === "string" && image && !image.startsWith("data:"))
      ? (image.startsWith("/") ? _origin + image : image)
      : "";
    upsertMeta("og:image", "property", absImage);
    upsertMeta("og:image:secure_url", "property", absImage);
    upsertMeta("og:image:alt", "property", absImage ? (articleMeta && articleMeta.imageAlt ? articleMeta.imageAlt : title) : "");
    upsertMeta("og:site_name", "property", siteName);
    upsertMeta("og:url", "property", ogUrl || canonical || (typeof window !== "undefined" ? window.location.href : ""));
    upsertMeta("og:locale", "property", ogLocale);
    upsertMeta("twitter:card", "name", absImage ? "summary_large_image" : "summary");
    upsertMeta("twitter:title", "name", title);
    upsertMeta("twitter:description", "name", description);
    upsertMeta("twitter:image", "name", absImage);
    upsertMeta("twitter:image:alt", "name", absImage ? (articleMeta && articleMeta.imageAlt ? articleMeta.imageAlt : title) : "");

    // Article-specific Open Graph (blog posts) — remove previous set then re-inject.
    document.head.querySelectorAll('meta[data-seo-article="1"]').forEach((n) => n.remove());
    if (articleMeta) {
      const addArt = (prop, val) => {
        if (!val) return;
        const m = document.createElement("meta");
        m.setAttribute("property", prop);
        m.setAttribute("content", val);
        m.setAttribute("data-seo-article", "1");
        document.head.appendChild(m);
      };
      addArt("article:published_time", articleMeta.publishedTime);
      addArt("article:modified_time", articleMeta.modifiedTime);
      addArt("article:author", articleMeta.author);
      (articleMeta.tags || []).forEach((t) => addArt("article:tag", t));
    }

    // Canonical
    const href = canonical || (typeof window !== "undefined" ? window.location.href : "");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", href);

    // Hreflang alternates — remove previous set then re-inject
    document.head.querySelectorAll('link[rel="alternate"][data-seo-hreflang="1"]').forEach((n) => n.remove());
    (alternates || []).forEach((a) => {
      if (!a.href || !a.hreflang) return;
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", a.hreflang);
      l.setAttribute("href", a.href);
      l.setAttribute("data-seo-hreflang", "1");
      document.head.appendChild(l);
    });

    // RSS/Atom feed autodiscovery
    document.head.querySelectorAll('link[rel="alternate"][data-seo-feed="1"]').forEach((n) => n.remove());
    if (feedHref) {
      const f = document.createElement("link");
      f.setAttribute("rel", "alternate");
      f.setAttribute("type", "application/rss+xml");
      f.setAttribute("title", `${siteName} — Blog`);
      f.setAttribute("href", feedHref);
      f.setAttribute("data-seo-feed", "1");
      document.head.appendChild(f);
    }

    // JSON-LD (supports single object or array)
    const existing = document.getElementById("jsonld-seo");
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "jsonld-seo";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById("jsonld-seo");
      if (s) s.remove();
      document.head.querySelectorAll('link[rel="alternate"][data-seo-hreflang="1"]').forEach((n) => n.remove());
      document.head.querySelectorAll('link[rel="alternate"][data-seo-feed="1"]').forEach((n) => n.remove());
      document.head.querySelectorAll('meta[data-seo-article="1"]').forEach((n) => n.remove());
    };
  }, [title, description, image, type, canonical, alternates, ogLocale, ogUrl, siteName, jsonLd, feedHref, articleMeta]);

  // Apply admin SEO overrides (title/description/noindex) by canonical path.
  useEffect(() => {
    let active = true;
    const href = canonical || (typeof window !== "undefined" ? window.location.href : "");
    let path = "";
    try { path = new URL(href).pathname; } catch (e) { return; }
    getOverrides().then((ov) => {
      if (!active) return;
      const o = ov[path] || ov[path.replace(/\/$/, "")] || ov[path + "/"];
      const DEFAULT_ROBOTS = "index, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
      let robots = document.head.querySelector('meta[name="robots"]');
      if (!robots) { robots = document.createElement("meta"); robots.setAttribute("name", "robots"); document.head.appendChild(robots); }
      robots.setAttribute("content", (noindex || (o && o.noindex)) ? "noindex,follow" : DEFAULT_ROBOTS);
      if (o && o.title) document.title = clampTitle(o.title);
      if (o && o.description) {
        const d = document.head.querySelector('meta[name="description"]');
        if (d) d.setAttribute("content", clampDesc(o.description));
      }
    });
    return () => { active = false; };
  }, [canonical, noindex]);

  return null;
}
